import { createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { getSlugRedirect, getContent } from "@/lib/cms.functions";
import { localePath, alternateLinks, normalizeLocale } from "@/lib/i18n";
import { ArticleView } from "@/components/site/ContentViews";
import { ClosingCta } from "@/components/site/Cta";
import { NotFoundBlock } from "@/components/site/NotFoundBlock";

export const Route = createFileRoute("/{-$locale}/integrations/$slug")({
  staticData: { sitemap: true },
  loader: async ({ params }) => {
    const locale = normalizeLocale((params as { locale?: string }).locale);
    const item = await getContent({ data: { kind: "integration", slug: params.slug, locale } });
    if (!item) {
      const current = await getSlugRedirect({ data: { kind: "integration", slug: params.slug, locale } });
      if (current) throw redirect({ href: localePath(locale, `/integrations/${encodeURIComponent(current)}`), statusCode: 301 });
      throw notFound();
    }
    return { locale, item };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Integration unavailable | factory.online" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { item } = loaderData;
    return {
      meta: [
        { title: `${item.seoTitle} | factory.online` },
        { name: "description", content: item.seoDescription },
        { property: "og:title", content: item.seoTitle },
        { property: "og:description", content: item.seoDescription },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: alternateLinks(`/integrations/${item.slug}`),
    };
  },
  notFoundComponent: IntegrationNotFound,
  component: IntegrationPage,
});

function IntegrationNotFound() {
  return (
    <NotFoundBlock
      title="Integration not found"
      lead="This integration page may have been moved or unpublished."
      backTo="/integration"
      backLabel="All integrations"
    />
  );
}

function IntegrationPage() {
  const { item, locale } = Route.useLoaderData();
  return (
    <>
      <ArticleView item={item} backTo="/integration" backLabel="All integrations" locale={locale} />
      <ClosingCta />
    </>
  );
}
